import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Briefcase,
  Users,
  Calendar,
  Clock,
  Plus,
  Trash2,
  Settings,
  Megaphone,
  AlertCircle,
  Plane,
  Ban,
  AlertTriangle,
  Info,
  Save,
  Loader2,
  Building2,
  Check,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = [
  { value: 1, label: 'Lundi', short: 'Lun' },
  { value: 2, label: 'Mardi', short: 'Mar' },
  { value: 3, label: 'Mercredi', short: 'Mer' },
  { value: 4, label: 'Jeudi', short: 'Jeu' },
  { value: 5, label: 'Vendredi', short: 'Ven' },
  { value: 6, label: 'Samedi', short: 'Sam' },
  { value: 7, label: 'Dimanche', short: 'Dim' }
];

const ANNOUNCEMENT_TYPES = {
  vacation: { icon: Plane, label: 'Congés / Vacances', color: 'blue' },
  absence: { icon: Ban, label: 'Absence', color: 'gray' },
  emergency: { icon: AlertTriangle, label: 'Parti pour urgence', color: 'red' },
  closure: { icon: X, label: 'Fermeture exceptionnelle', color: 'orange' },
  reduced_hours: { icon: Clock, label: 'Horaires réduits', color: 'yellow' },
  info: { icon: Info, label: 'Information', color: 'blue' },
  custom: { icon: Megaphone, label: 'Message personnalisé', color: 'purple' }
};

const MODE_LABELS = {
  disabled: "Ne reçoit pas les représentants de laboratoires",
  specific_slots: "Créneaux dédiés uniquement",
  like_patient: "Comme un patient normal"
};

export const MedicalRepConfig = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [showAddSlot, setShowAddSlot] = useState(false);
  const [newSlot, setNewSlot] = useState({ day_of_week: 1, start_time: '14:00', end_time: '16:00' });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/medical-rep/config`, { headers });
      setConfig(response.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConfig(); }, []);

  const updateConfig = async (updates) => {
    setSaving(true);
    try {
      const response = await axios.put(`${API_URL}/api/medical-rep/config`, updates, { headers });
      setConfig(response.data.config);
      toast.success('Configuration mise à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const addSlot = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/medical-rep/slots`, newSlot, { headers });
      setConfig(response.data.config);
      setShowAddSlot(false);
      setNewSlot({ day_of_week: 1, start_time: '14:00', end_time: '16:00' });
      toast.success('Créneau ajouté');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const removeSlot = async (slotId) => {
    try {
      const response = await axios.delete(`${API_URL}/api/medical-rep/slots/${slotId}`, { headers });
      setConfig(response.data.config);
      toast.success('Créneau supprimé');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="medical-rep-config">
      {/* Header */}
      <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Briefcase className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <CardTitle>Représentants de Laboratoires</CardTitle>
              <CardDescription>Configurez comment vous recevez les visiteurs médicaux</CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Mode Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Mode de réception</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {Object.entries(MODE_LABELS).map(([mode, label]) => (
            <div
              key={mode}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                config?.mode === mode 
                  ? 'border-purple-500 bg-purple-50' 
                  : 'border-gray-200 hover:border-purple-200'
              }`}
              onClick={() => updateConfig({ mode })}
              data-testid={`mode-${mode}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {mode === 'disabled' && <Ban className="w-5 h-5 text-gray-500" />}
                  {mode === 'specific_slots' && <Calendar className="w-5 h-5 text-purple-500" />}
                  {mode === 'like_patient' && <Users className="w-5 h-5 text-green-500" />}
                  <span className="font-medium">{label}</span>
                </div>
                {config?.mode === mode && <Check className="w-5 h-5 text-purple-600" />}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Specific Slots (if mode = specific_slots) */}
      {config?.mode === 'specific_slots' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Créneaux dédiés aux représentants
              </CardTitle>
              <Dialog open={showAddSlot} onOpenChange={setShowAddSlot}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="add-slot-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nouveau créneau représentants de laboratoires</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>Jour</Label>
                      <Select
                        value={String(newSlot.day_of_week)}
                        onValueChange={(v) => setNewSlot({ ...newSlot, day_of_week: parseInt(v) })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {DAYS_OF_WEEK.map(d => (
                            <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Début</Label>
                        <Input
                          type="time"
                          value={newSlot.start_time}
                          onChange={(e) => setNewSlot({ ...newSlot, start_time: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Fin</Label>
                        <Input
                          type="time"
                          value={newSlot.end_time}
                          onChange={(e) => setNewSlot({ ...newSlot, end_time: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowAddSlot(false)}>Annuler</Button>
                    <Button onClick={addSlot}>Ajouter</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {config?.specific_slots?.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">Aucun créneau configuré</p>
            ) : (
              <div className="space-y-2">
                {config?.specific_slots?.map((slot) => (
                  <div key={slot.id} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline">{DAYS_OF_WEEK.find(d => d.value === slot.day_of_week)?.label}</Badge>
                      <span>{slot.start_time} - {slot.end_time}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => removeSlot(slot.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Settings (if not disabled) */}
      {config?.mode !== 'disabled' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Paramètres
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Durée max (minutes)</Label>
                <Input
                  type="number"
                  value={config?.max_duration_minutes || 30}
                  onChange={(e) => updateConfig({ max_duration_minutes: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <Label>Max par semaine</Label>
                <Input
                  type="number"
                  value={config?.max_per_week || 5}
                  onChange={(e) => updateConfig({ max_per_week: parseInt(e.target.value) })}
                />
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium">Confirmation automatique</p>
                <p className="text-sm text-muted-foreground">Les RDV sont confirmés sans votre validation</p>
              </div>
              <Switch
                checked={config?.auto_confirm || false}
                onCheckedChange={(checked) => updateConfig({ auto_confirm: checked })}
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium">Exiger infos entreprise</p>
                <p className="text-sm text-muted-foreground">Le représentant doit fournir le nom de son entreprise</p>
              </div>
              <Switch
                checked={config?.require_company_info !== false}
                onCheckedChange={(checked) => updateConfig({ require_company_info: checked })}
              />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export const DoctorAnnouncements = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newAnn, setNewAnn] = useState({
    announcement_type: 'vacation',
    title: '',
    message: '',
    start_date: '',
    end_date: '',
    affects_availability: true,
    show_on_calendar: true
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/doctor-announcements?include_expired=true`, { headers });
      setAnnouncements(response.data.announcements || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAnnouncements(); }, []);

  const createAnnouncement = async () => {
    if (!newAnn.title || !newAnn.start_date) {
      toast.error('Titre et date de début requis');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/doctor-announcements`, newAnn, { headers });
      toast.success('Annonce créée');
      setShowCreate(false);
      setNewAnn({
        announcement_type: 'vacation',
        title: '',
        message: '',
        start_date: '',
        end_date: '',
        affects_availability: true,
        show_on_calendar: true
      });
      fetchAnnouncements();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const deleteAnnouncement = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/doctor-announcements/${id}`, { headers });
      toast.success('Annonce supprimée');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const toggleActive = async (ann) => {
    try {
      await axios.put(`${API_URL}/api/doctor-announcements/${ann.id}`, 
        { is_active: !ann.is_active }, 
        { headers }
      );
      fetchAnnouncements();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="doctor-announcements">
      {/* Header */}
      <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Megaphone className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <CardTitle>Annonces & Messages</CardTitle>
                <CardDescription>Informez vos patients de vos absences, congés et fermetures</CardDescription>
              </div>
            </div>
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
              <DialogTrigger asChild>
                <Button data-testid="create-announcement-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvelle annonce
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Créer une annonce</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label>Type</Label>
                    <Select
                      value={newAnn.announcement_type}
                      onValueChange={(v) => setNewAnn({ ...newAnn, announcement_type: v })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(ANNOUNCEMENT_TYPES).map(([key, val]) => {
                          const Icon = val.icon;
                          return (
                            <SelectItem key={key} value={key}>
                              <div className="flex items-center gap-2">
                                <Icon className="w-4 h-4" />
                                {val.label}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Titre</Label>
                    <Input
                      value={newAnn.title}
                      onChange={(e) => setNewAnn({ ...newAnn, title: e.target.value })}
                      placeholder="Ex: Congés annuels"
                    />
                  </div>
                  <div>
                    <Label>Message (optionnel)</Label>
                    <Textarea
                      value={newAnn.message}
                      onChange={(e) => setNewAnn({ ...newAnn, message: e.target.value })}
                      placeholder="Détails supplémentaires pour vos patients..."
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Date début</Label>
                      <Input
                        type="date"
                        value={newAnn.start_date}
                        onChange={(e) => setNewAnn({ ...newAnn, start_date: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Date fin (optionnel)</Label>
                      <Input
                        type="date"
                        value={newAnn.end_date}
                        onChange={(e) => setNewAnn({ ...newAnn, end_date: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Bloque les disponibilités</Label>
                      <Switch
                        checked={newAnn.affects_availability}
                        onCheckedChange={(c) => setNewAnn({ ...newAnn, affects_availability: c })}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Visible sur le calendrier</Label>
                      <Switch
                        checked={newAnn.show_on_calendar}
                        onCheckedChange={(c) => setNewAnn({ ...newAnn, show_on_calendar: c })}
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowCreate(false)}>Annuler</Button>
                  <Button onClick={createAnnouncement}>Créer</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
      </Card>

      {/* Announcements List */}
      <Card>
        <CardContent className="pt-6">
          {announcements.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Aucune annonce</p>
          ) : (
            <div className="space-y-3">
              {announcements.map((ann) => {
                const typeInfo = ANNOUNCEMENT_TYPES[ann.announcement_type] || ANNOUNCEMENT_TYPES.custom;
                const Icon = typeInfo.icon;
                const isExpired = ann.end_date && new Date(ann.end_date) < new Date();
                
                return (
                  <div 
                    key={ann.id}
                    className={`p-4 rounded-lg border ${
                      !ann.is_active || isExpired
                        ? 'bg-gray-50 border-gray-200 opacity-60'
                        : 'bg-white border-gray-200'
                    }`}
                    data-testid={`announcement-${ann.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg bg-${typeInfo.color}-100`}>
                          <Icon className={`w-5 h-5 text-${typeInfo.color}-600`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">{ann.title}</h4>
                            <Badge variant={ann.is_active ? "default" : "secondary"}>
                              {ann.is_active ? "Active" : "Inactive"}
                            </Badge>
                            {isExpired && <Badge variant="outline">Expirée</Badge>}
                          </div>
                          {ann.message && <p className="text-sm text-muted-foreground mt-1">{ann.message}</p>}
                          <p className="text-xs text-muted-foreground mt-2">
                            {ann.start_date} {ann.end_date ? `→ ${ann.end_date}` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={ann.is_active}
                          onCheckedChange={() => toggleActive(ann)}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600"
                          onClick={() => deleteAnnouncement(ann.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Combined Component for Professional Dashboard
const MedicalRepAndAnnouncements = () => {
  return (
    <Tabs defaultValue="medical-rep" className="w-full">
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="medical-rep" className="flex items-center gap-2">
          <Briefcase className="w-4 h-4" />
          Représentants Labo
        </TabsTrigger>
        <TabsTrigger value="announcements" className="flex items-center gap-2">
          <Megaphone className="w-4 h-4" />
          Annonces
        </TabsTrigger>
      </TabsList>
      <TabsContent value="medical-rep" className="mt-6">
        <MedicalRepConfig />
      </TabsContent>
      <TabsContent value="announcements" className="mt-6">
        <DoctorAnnouncements />
      </TabsContent>
    </Tabs>
  );
};

export default MedicalRepAndAnnouncements;
